import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';

export default function EmpTypeList() {
    const { http, user } = AuthUser();

    const [loading, setLoading] = useState(true);
    const [empTypes, setEmpTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formName, setFormName] = useState('');

    useEffect(() => {
        fetchEmpTypes();
    }, []);

    const fetchEmpTypes = async () => {
        try {
            const response = await http.get(`/emp_types?login_user=${user.id}`);
            if (response.data.success) {
                setEmpTypes(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching employee types:', error);
            toast.error('Error loading employee types');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formName.trim()) {
            toast.error('Please enter a name');
            return;
        }

        try {
            const requestData = { name: formName.trim(), login_user: user.id };
            let response;
            if (editingType) {
                response = await http.put(`/emp_type/${editingType.id}`, requestData);
            } else {
                response = await http.post('/emp_type', requestData);
            }

            if (response.data.success) {
                toast.success(editingType ? 'Updated successfully!' : 'Created successfully!');
                setShowModal(false);
                setEditingType(null);
                setFormName('');
                fetchEmpTypes();
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Error saving record');
        }
    };

    const handleEdit = (empType) => {
        setEditingType(empType);
        setFormName(empType.name || '');
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this employee type?')) return;
        try {
            const response = await http.delete(`/emp_type/${id}`);
            if (response.data.success) {
                toast.success('Deleted successfully!');
                fetchEmpTypes();
            }
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error('Error deleting record');
        }
    };

    const openAddModal = () => {
        setEditingType(null);
        setFormName('');
        setShowModal(true);
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-0"><i className="fa fa-id-badge me-2"></i>Employee Types</h5>
                        <small className="text-muted">Manage employee types used in Salary Master</small>
                    </div>
                    <button className="btn btn-primary" onClick={openAddModal}>
                        <i className="fa fa-plus me-1"></i>Add New
                    </button>
                </div>

                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : empTypes.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fa fa-inbox fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                                <p className="mb-0">No employee types found. Add one to get started.</p>
                            </div>
                        ) : (
                            <table className="table table-hover table-sm mb-0">
                                <thead style={{ backgroundColor: '#f8f9fa' }}>
                                    <tr>
                                        <th style={{ width: '60px' }}>#</th>
                                        <th>Name</th>
                                        <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {empTypes.map((et, idx) => (
                                        <tr key={et.id}>
                                            <td>{idx + 1}</td>
                                            <td><strong>{et.name}</strong></td>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-1 py-0"
                                                    onClick={() => handleEdit(et)}
                                                    title="Edit"
                                                >
                                                    <i className="fa fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger py-0"
                                                    onClick={() => handleDelete(et.id)}
                                                    title="Delete"
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    <i className={`fa ${editingType ? 'fa-edit' : 'fa-plus-circle'} me-2`}></i>
                                    {editingType ? 'Edit' : 'Add'} Employee Type
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-0">
                                        <label className="form-label fw-semibold">Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formName}
                                            onChange={(e) => setFormName(e.target.value)}
                                            placeholder="e.g., Permanent, Contract, Probation"
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary btn-sm">
                                        <i className="fa fa-save me-1"></i>
                                        {editingType ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
